import React from 'react';
import { useSelector } from 'react-redux';
import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useLocation } from 'react-router-dom';
import CustomNavLink from './CustomNavLink';
import { paths } from '@/app/routes';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

export const SideBarListItem = ({ item }) => {
	const theme = useTheme();

	const { isCollapsed } = useSelector((state) => state.sideNavSlice);

	const { themeMode, invertSideNavColors } = useSelector((state) => state.userPrefs);
	const isLightMode = themeMode !== 'dark';
	const inverted = isLightMode && invertSideNavColors;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const isWinbeatLedger = userDetails?.isWinbeatLedger;

	const itemColor = inverted ? theme.palette.sideNav : 'whitesmoke';

	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const location = useLocation();

	const collapsed = isCollapsed && !isMobile;
	const basePath = paths.clients;

	const isSelected = item.link
		? item.link === paths.home
			? location.pathname.startsWith(basePath) || location.pathname === paths.home
			: item.link === paths.relationships
				? location.pathname.startsWith(item.link) ||
					location.pathname.startsWith(paths.companies)
				: location.pathname.startsWith(item.link)
		: false;

	const displayColor = isSelected ? theme.palette.common.white : itemColor;

	const icon = React.cloneElement(item.icon, { color: displayColor });

	/**
	 * If item.onClick is present, we run that function (instead of navigating).
	 * Otherwise, we let <CustomNavLink> handle navigation as before.
	 */
	const handleClick = (event) => {
		if (item.onClick) {
			// Prevent navigation if there's an onClick
			event.preventDefault();
			item.onClick();
		}
	};

	return (
		<ListItem key={item.text} disableGutters={collapsed} disablePadding>
			<Tooltip title={collapsed ? item.text : ''} placement='right'>
				<ListItemButton
					// If there's an onClick, we'll just use a 'div' for the component
					// so it won't attempt to navigate. Otherwise, use CustomNavLink.
					component={item.onClick ? 'div' : CustomNavLink}
					to={item.onClick ? undefined : item.link}
					onClick={handleClick}
					rel={item.rel ? item.rel : undefined}
					target={item.target ? item.target : undefined}
					variant={isWinbeatLedger ? 'side-nav-winbeat' : 'side-nav'}
					sx={{
						color: displayColor,
						'& .MuiSvgIcon-root': {
							fill: displayColor,
						},
						'& .MuiListItemIcon-root': {
							color: displayColor,
						},
						...(collapsed && {
							paddingLeft: 0,
							paddingRight: 0,
							justifyContent: 'center',
						}),
					}}
					className={isSelected ? 'Mui-selected' : ''}
				>
					<ListItemIcon
						sx={{
							...(collapsed && {
								minWidth: '24px',
								display: 'flex',
								justifyContent: 'center',
							}),
						}}
					>
						{icon}
					</ListItemIcon>
					{!collapsed && (
						<ListItemText
							primary={item.text}
							sx={{ fontWeight: 400, marginLeft: '-12px' }}
						/>
					)}
				</ListItemButton>
			</Tooltip>
		</ListItem>
	);
};
